import * as React from 'react';

import { ButtonType, ButtonWidget, InteractionState } from '@tableau/widgets';

import { CommonStyles } from '../../common/react/CommonStyles';
import { imgPath } from '../../common/utilities/ImgUrl';
import { ReactDirectiveProps } from '../../ts/directives/reactDirectiveFactory';
import { HeaderSearchBox } from '../header/HeaderSearchBox';
import { PageHeaderButtonType, PageHeaderIconPaths, PageHeaderIcons } from './PageHeader';

export const vizHeaderSearchButtonImgTestId = 'viz-header-search-button-img';

export interface SearchProps extends ReactDirectiveProps {
  headerSearchStyle: string;
  searchClosed: (event: BaseJQueryEventObject) => void;
  searchClosedWithoutNeedingFocus: (event: BaseJQueryEventObject) => void;
  letSearchBoxHasFocus?: boolean;
  isSearchInputBoxActive: (isActive: boolean) => void;
}

export function PageHeaderSearch(props: SearchProps): JSX.Element {
  return (
    <div className={props.headerSearchStyle}>
      <HeaderSearchBox
        angularContext={props.angularContext}
        omniboxOpenWidth={350}
        searchClosed={props.searchClosed}
        searchClosedWithoutNeedingFocus={props.searchClosedWithoutNeedingFocus}
        isSearchOpen={true}
        letSearchBoxHasFocus={props.letSearchBoxHasFocus}
        isSearchInputBoxActive= {props.isSearchInputBoxActive}
      />
    </div>
  );
}

const glassIcons: PageHeaderIcons = {
  lightIcons: {
    active: imgPath('spaces_toolbar_search_active_white.svg'),
    inactive: imgPath('spaces_toolbar_search_white.svg')
  },
  regularIcons: {
    active: imgPath('spaces_toolbar_search_active.svg'),
    inactive: imgPath('spaces_toolbar_search.svg')
  }
};

const closeIcons: PageHeaderIcons = {
  lightIcons: {
    active: imgPath('spaces_toolbar_delete_active_white.svg'),
    inactive: imgPath('spaces_toolbar_delete_white.svg')
  },
  regularIcons: {  // this should never need non-white icons
    active: imgPath('spaces_toolbar_delete_active_white.svg'),
    inactive: imgPath('spaces_toolbar_delete_white.svg')
  }
};

interface IconInfo {
  src: string;
  width: number;
  height: number;
}

function buttonIconInfo(isSearchOpen: boolean, showActiveIcon: boolean, buttonType: PageHeaderButtonType): IconInfo {
  if (isSearchOpen) {
    const icons: PageHeaderIconPaths = closeIcons.lightIcons;
    return {
      width: 32,
      height: 32,
      src: showActiveIcon ? icons.active : icons.inactive
    };

  } else { // search is not open
    const icons: PageHeaderIconPaths = (buttonType === 'light') ? glassIcons.lightIcons : glassIcons.regularIcons;
    return {
      width: 32,
      height: 32,
      src: showActiveIcon ? icons.active : icons.inactive
    };
  }
}

const getButtonContent = (isSearchOpen: boolean, buttonType: PageHeaderButtonType): (state: InteractionState) => JSX.Element => {
  return (state: InteractionState): JSX.Element => {
    const showActiveIcon = state.hover || state.active || state.focus;
    const iconInfo = buttonIconInfo(isSearchOpen, showActiveIcon, buttonType);
    let iconStyle: React.CSSProperties = {width: iconInfo.width, height: iconInfo.height, border: '1px solid transparent', marginRight: '0px'};

     // Keep alt text blank since button has a title: TFS 1036309
    return (
      <div style={{...CommonStyles.Layout.FlexBlockLeftAndCenter}}>
        <img
          style={iconStyle}
          src={iconInfo.src}
          data-tb-test-id={vizHeaderSearchButtonImgTestId}
          alt=""
        />
      </div>
    );
  };
};

export interface VizViewerSearchButtonProps {
  readonly isSearchOpen: boolean;
  readonly title: string;
  readonly testId: string;
  readonly onClick: () => void;
  readonly setRef: (button: ButtonWidget) => void;
}

export function VizViewerSearchButton(props: VizViewerSearchButtonProps): JSX.Element {
  return (
    <div style={{marginTop: '9px', marginRight: '-2px'}}>
      <ButtonWidget
        buttonType={ButtonType.IconOutline}
        getContent={getButtonContent(props.isSearchOpen, 'light')}
        title={props.title}
        testId={props.testId}
        handleClick={props.onClick}
        ref={props.setRef}
        style={{cursor: 'pointer'}}
      />
    </div>
  );
}
